<template>
  <!-- begin:: Header -->
  <div
    id="kt_header"
    ref="kt_header"
    class="header"
    v-bind:class="[headerClasses, { 'sa-header': isSuperAdmin, 'biz-header': !isSuperAdmin }]"
    :style="headerStyle"
  >
    <div
      class="container-fluid"
      :class="isSuperAdmin ? 'sa-header-inner tw-flex tw-h-full tw-items-center tw-justify-between tw-px-5' : 'biz-header-inner d-flex align-items-center justify-content-between tw-flex tw-h-full tw-items-center tw-justify-between tw-gap-3'"
    >
      <template v-if="isSuperAdmin">
        <div class="sa-header-left">
          <button type="button" class="sa-burger" aria-label="Menu" @click="toggleSaMenu">
            <i class="mdi mdi-menu"></i>
          </button>
        </div>
        <div class="sa-header-search">
          <i class="mdi mdi-magnify"></i>
          <input
            v-model="saSearch"
            type="text"
            placeholder="Search businesses..."
            @keyup.enter="runSaSearch"
          />
        </div>
        <div class="sa-header-right">
          <button type="button" class="sa-user-chip" @click="openSaQuickUser">
            <span class="sa-avatar">{{ saUserInitial }}</span>
            <span class="sa-user-meta">
              <span class="sa-user-name">{{ saUserName }}</span>
              <span class="sa-user-role">Super Admin</span>
            </span>
            <i class="mdi mdi-chevron-down sa-user-caret"></i>
          </button>
          <KTQuickUser ref="saQuickUser" :hide-toggle="true"></KTQuickUser>
        </div>
      </template>
      <template v-else>
        <div
          class="header-menu-wrapper header-menu-wrapper-left d-flex align-items-center biz-header-left"
          ref="kt_header_menu_wrapper"
        >
          <div
            v-if="headerMenuEnabled"
            id="kt_header_menu"
            ref="kt_header_menu"
            class="header-menu header-menu-mobile"
            v-bind:class="headerMenuClasses"
          >
            <KTMenu></KTMenu>
          </div>
          <h2 class="biz-header-title tw-m-0 tw-truncate">
            {{ currentUserPersonalInfo.business_name }}
          </h2>
          <!-- Global plan status (subheader is hidden for business theme) -->
          <SubscriptionStatusBanner />
        </div>
        <KTTopbar></KTTopbar>
      </template>
    </div>
  </div>
  <!-- end:: Header -->
</template>

<script>
import { mapGetters } from "vuex";
import KTTopbar from "@/view/layout/header/Topbar.vue";
import KTQuickUser from "@/view/layout/extras/offcanvas/QuickUser.vue";
import KTLayoutHeader from "@/assets/js/layout/base/header.js";
import KTLayoutHeaderMenu from "@/assets/js/layout/base/header-menu.js";
import KTMenu from "@/view/layout/header/Menu.vue";
import SubscriptionStatusBanner from "@/view/layout/header/SubscriptionStatusBanner.vue";
import { BUSINESS_DETAILS } from "@/core/services/store/config.module";

export default {
  name: "KTHeader",
  data() {
    return {
      business_name : "",
      store_name : '',
      saSearch: '',
    };
  },
  components: {
    KTTopbar,
    KTQuickUser,
    KTMenu,
    SubscriptionStatusBanner,
  },
  mounted() {
    KTLayoutHeader.init("kt_header", "kt_header_mobile");

    if (this.$refs["kt_header_menu"]) {
      KTLayoutHeaderMenu.init(
        this.$refs["kt_header_menu"],
        this.$refs["kt_header_menu_wrapper"]
      );

      const headerRef = this.$refs["kt_header"];
      headerRef.querySelectorAll("a[class='menu-link']").forEach(item => {
        item.addEventListener("click", () => {
          KTLayoutHeaderMenu.getOffcanvas().hide();
        });
      });
    }
  },
  methods : {
    openSaQuickUser() {
      if (this.$refs.saQuickUser && this.$refs.saQuickUser.toggleQuickUser) {
        this.$refs.saQuickUser.toggleQuickUser();
      }
    },
    runSaSearch() {
      const q = (this.saSearch || '').trim();
      this.$router.push({ path: '/admin/businesses', query: q ? { q } : {} });
    },
    toggleSaMenu() {
      // Desktop minimize + mobile offcanvas
      if (window.innerWidth <= 991) {
        document.body.classList.toggle('mobile-toggle-active');
        const aside = document.getElementById('kt_aside');
        if (!aside) return;
        aside.classList.toggle('offcanvas-on');
        const existing = document.querySelector('.aside-offcanvas-overlay');
        if (aside.classList.contains('offcanvas-on')) {
          if (!existing) {
            const overlay = document.createElement('div');
            overlay.className = 'aside-offcanvas-overlay';
            overlay.addEventListener('click', () => {
              aside.classList.remove('offcanvas-on', 'aside-on');
              document.body.classList.remove('mobile-toggle-active');
              overlay.remove();
            });
            if (aside.parentNode) {
              aside.parentNode.insertBefore(overlay, aside.nextSibling);
            } else {
              document.body.appendChild(overlay);
            }
          }
        } else if (existing) {
          existing.remove();
        }
      } else {
        const toggle = document.getElementById('kt_aside_toggle');
        if (toggle) toggle.click();
      }
    },
    getBusinessName() {
      this.$store
        .dispatch(BUSINESS_DETAILS)
        .then((resp) => {
            this.business_name = resp.data.name;
            this.store_name = resp.data.store_name;
        })
        .catch((error) => {
          console.error(error);
        });
    }
  },
  computed: {
    ...mapGetters(["layoutConfig", "getClasses", "currentUserPersonalInfo"]),

    isSuperAdmin() {
      return this.$store.state.auth.isAuthenticated && !this.$store.state.auth.normal;
    },

    saUserName() {
      const info = this.currentUserPersonalInfo || {};
      const user = (this.$store.state.auth && this.$store.state.auth.user) || {};
      return info.name || user.name || "Admin";
    },

    saUserInitial() {
      return String(this.saUserName || "A").charAt(0).toUpperCase();
    },

    headerStyle() {
      return {
        background: "#ffffff",
        boxShadow: "0 1px 0 rgba(15, 23, 42, 0.06)"
      };
    },

    headerMenuEnabled() {
      return !!this.layoutConfig("header.menu.self.display");
    },

    headerClasses() {
      const classes = this.getClasses("header");
      if (typeof classes !== "undefined") {
        return classes.join(" ");
      }
      return null;
    },

    headerMenuClasses() {
      const classes = this.getClasses("header_menu");
      if (typeof classes !== "undefined") {
        return classes.join(" ");
      }
      return null;
    }
  }
};
</script>

<style>
/* Unscoped so profile is never clipped by Vue scoped attrs / Metronic topbar rules */
.sa-header.header {
  width: auto !important;
  max-width: none !important;
  right: 0 !important;
  left: 256px !important;
  overflow: visible !important;
}

body.aside-minimize:not(.aside-minimize-hover) .sa-header.header {
  left: 70px !important;
}

body.aside-minimize-hover .sa-header.header {
  left: 256px !important;
}

@media (max-width: 991.98px) {
  .sa-header.header {
    left: 0 !important;
  }
}

.sa-header .sa-header-inner {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  height: 100% !important;
  padding: 0 20px !important;
  gap: 12px !important;
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box !important;
  overflow: visible !important;
  flex: 1 1 auto !important;
  min-width: 0 !important;
}

.sa-header .sa-header-left {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  flex: 0 0 auto !important;
}

.sa-header .sa-burger {
  border: none !important;
  background: transparent !important;
  color: #334155 !important;
  width: 40px !important;
  height: 40px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 10px !important;
  cursor: pointer !important;
  padding: 0 !important;
}

.sa-header .sa-burger i {
  font-size: 26px !important;
  line-height: 1 !important;
  color: #334155 !important;
}

.sa-header .sa-header-title {
  margin: 0 !important;
  color: #16124a !important;
  text-transform: none !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  letter-spacing: -0.02em !important;
  white-space: nowrap !important;
  font-family: Inter, ui-sans-serif, sans-serif !important;
}

.sa-header .sa-header-search {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
  flex: 1 1 auto !important;
  max-width: 520px !important;
  min-width: 160px !important;
  margin: 0 auto !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 999px !important;
  padding: 8px 16px !important;
  height: 42px !important;
}

.sa-header .sa-header-search i {
  color: #94a3b8 !important;
  font-size: 20px !important;
}

.sa-header .sa-header-search input {
  border: none !important;
  background: transparent !important;
  outline: none !important;
  width: 100% !important;
  font-size: 14px !important;
  color: #64748b !important;
}

.sa-header .sa-header-right {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 12px !important;
  flex: 0 0 auto !important;
  margin-left: auto !important;
  position: relative !important;
  z-index: 40 !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.sa-header .sa-notify {
  position: relative !important;
  width: 42px !important;
  height: 42px !important;
  border-radius: 12px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  cursor: pointer !important;
  padding: 0 !important;
}

.sa-header .sa-notify i {
  font-size: 22px !important;
  color: #475569 !important;
}

.sa-header .sa-notify-badge {
  position: absolute !important;
  top: -4px !important;
  right: -4px !important;
  min-width: 18px !important;
  height: 18px !important;
  border-radius: 999px !important;
  background: #241B5A !important;
  color: #fff !important;
  font-size: 10px !important;
  font-weight: 700 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 4px !important;
}

.sa-header .sa-user-chip {
  display: inline-flex !important;
  align-items: center !important;
  gap: 10px !important;
  cursor: pointer !important;
  padding: 4px 10px 4px 4px !important;
  border-radius: 999px !important;
  border: 1px solid #e2e8f0 !important;
  background: #fff !important;
}

.sa-header .sa-user-chip:hover {
  background: #f8fafc !important;
}

.sa-header .sa-avatar {
  width: 36px !important;
  height: 36px !important;
  border-radius: 50% !important;
  background: #241B5A !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: #fff !important;
  font-size: 12px !important;
  font-weight: 700 !important;
}

.sa-header .sa-user-meta {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  line-height: 1.15 !important;
}

.sa-header .sa-user-name {
  font-size: 13px !important;
  font-weight: 700 !important;
  color: #1e293b !important;
}

.sa-header .sa-user-role {
  font-size: 11px !important;
  color: #94a3b8 !important;
}

.sa-header .sa-user-caret {
  color: #94a3b8 !important;
  font-size: 18px !important;
}

.sa-header .topbar-item {
  width: auto !important;
  height: auto !important;
  overflow: visible !important;
  margin: 0 !important;
  padding: 0 !important;
  position: static !important;
}

.sa-header #kt_quick_user,
.biz-header #kt_quick_user {
  position: fixed !important;
  z-index: 10050 !important;
}

@media (max-width: 991px) {
  .sa-header .sa-header-search {
    display: none !important;
  }
  .sa-header .sa-user-meta,
  .sa-header .sa-user-caret {
    display: none !important;
  }
}

/* Business header — white bar like SA */
.biz-header.header {
  width: auto !important;
  max-width: none !important;
  right: 0 !important;
  left: 275px !important;
  overflow: visible !important;
  background: #ffffff !important;
  z-index: 104 !important;
}

body.aside-minimize:not(.aside-minimize-hover) .biz-header.header {
  left: 70px !important;
}

body.aside-minimize-hover .biz-header.header {
  left: 275px !important;
}

@media (max-width: 991.98px) {
  .biz-header.header {
    left: 0 !important;
  }
}

.biz-header .biz-header-inner {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  height: 100% !important;
  padding: 0 25px !important;
  gap: 12px !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

.biz-header .biz-header-title {
  margin: 0 !important;
  font-family: "Inter", ui-sans-serif, system-ui, sans-serif !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #16124a !important;
  letter-spacing: -0.02em !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: min(520px, 55vw) !important;
}

.biz-header .topbar {
  position: static !important;
  height: auto !important;
  background: transparent !important;
  box-shadow: none !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}

.biz-header .topbar .globe-icon,
.biz-header .topbar .responsive-text,
.biz-header .topbar .mdi,
.biz-header .topbar .fa,
.biz-header .topbar .quick-user-toggle i {
  color: #475569 !important;
}

.biz-header .topbar .globe-icon {
  width: 42px !important;
  height: 42px !important;
  border-radius: 12px !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 18px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.biz-header .topbar .branch-selector {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  backdrop-filter: none !important;
}

.biz-header .topbar .branch-label {
  color: #475569 !important;
}

.biz-header .topbar .quick-user-toggle {
  width: 42px !important;
  height: 42px !important;
  border-radius: 12px !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 0 !important;
}

.biz-header .topbar .quick-user-toggle i {
  color: #475569 !important;
  font-size: 20px !important;
}

.sa-header.header {
  background: #ffffff !important;
  border-bottom: 1px solid #eef1f6 !important;
}

.sa-header .sa-header-title {
  color: #0f172a !important;
}

.sa-header .sa-burger {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
}

.sa-header .sa-burger i {
  color: #0f172a !important;
}

.sa-header .sa-user-chip {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
}

.sa-header .sa-user-name {
  color: #0f172a !important;
}

.sa-header .sa-user-role,
.sa-header .sa-user-caret {
  color: #64748b !important;
}
</style>

<style scoped>
.text-primary {
  color: #241B5A !important;
}
.bg-primary {
  background-color: #241B5A !important;
}
</style>
